import { Injectable } from '@angular/core';
import { BaseFileClientProviderService } from '../base-file-client-provider.service';
import { IGitClient } from 'app/services/git/IGitClient';
import { IPublishConfig } from './publisher.datatypes';
import { convertObjectToString, stringIsNullOrEmpty, joinPaths } from 'app/shared/utils/string.util';
import { LoggerService } from 'app/logger/logger.service';
import { MetadataModeService } from 'app/services/metadata-mode.service';
import { ISaveResult, SaveStatus } from 'app/interfaces/ISaveStatus';

import { DEFAULT_PUBLISH_BRANCH } from 'app/services/git/git-models';

@Injectable({
    providedIn: 'root'
})
export class PublishConfigService {
    public static readonly publishConfigFileName = 'publish_config.json';
    protected readonly className = 'PublishConfigService';
    private readonly _defaultConfig: IPublishConfig = {
        publishBranch: DEFAULT_PUBLISH_BRANCH
    };

    private _publishConfig: IPublishConfig;
    private _isInitializing: boolean;
    private _hitLoadError: boolean;

    public get publishConfig(): IPublishConfig {
        return this._publishConfig || this._defaultConfig;
    }

    public get hitLoadError(): boolean {
        return this._hitLoadError;
    }

    private get _client(): IGitClient {
        return this._clientProvider.client;
    }

    private get _rootFolder(): string {
        return this._client.getRootFolder();
    }

    constructor(
        private readonly _clientProvider: BaseFileClientProviderService<IGitClient>,
        private readonly _metadataModeService: MetadataModeService,
        private readonly _logger: LoggerService
    ) {
    }

    public async initialize(force: boolean = false): Promise<IPublishConfig> {
        if ((force || !this._publishConfig) && !this._isInitializing && !this._metadataModeService.isLiveMode()) {
            await this._getLatestPublishConfig();
        }
        return this.publishConfig;
    }

    public async updatePublishBranch(publishBranch: string): Promise<ISaveResult> {
        const newConfig = {...this.publishConfig };
        newConfig.publishBranch = publishBranch;
        return await this._updatePublishConfig(newConfig);
    }

    public async updateIncludeFactoryTemplate(includeFactoryTemplate: boolean): Promise<ISaveResult> {
        const newConfig = {...this.publishConfig };
        newConfig.includeFactoryTemplate = includeFactoryTemplate;
        this._logger.log(this.className, `Updating includeFactoryTemplate, value: ${includeFactoryTemplate}`);
        return await this._updatePublishConfig(newConfig);
    }

    public async updateEnableGitComment(enableGitComment: boolean): Promise<ISaveResult> {
        const newConfig = {...this.publishConfig };
        newConfig.enableGitComment = enableGitComment;
        this._logger.log(this.className, `Updating enableGitComment, value: ${enableGitComment}`);
        return await this._updatePublishConfig(newConfig);
    }

    public async updateIncludeCmkSettings(includeCmkSettings: boolean): Promise<ISaveResult> {
        const newConfig = {...this.publishConfig };
        newConfig.includeFactoryTemplateWithCmkSettings = includeCmkSettings;
        this._logger.log(this.className, `Updating includeCmkSettings, value: ${includeCmkSettings}`);
        return await this._updatePublishConfig(newConfig);
    }

    public async updateIncludeGlobalParams(includeGlobalParams: boolean): Promise<ISaveResult> {
        const newConfig = {...this.publishConfig };
        newConfig.includeGlobalParamsTemplate = includeGlobalParams;
        if (includeGlobalParams && newConfig.includeFactoryTemplate) {
            newConfig.includeFactoryTemplate = !includeGlobalParams;
        }
        if (includeGlobalParams && newConfig.includeFactoryTemplateWithCmkSettings) {
            newConfig.includeFactoryTemplateWithCmkSettings = !includeGlobalParams;
        }
        this._logger.log(this.className, `Updating includeGlobalParams, value: ${includeGlobalParams}`);
        return await this._updatePublishConfig(newConfig);
    }

    private async _getLatestPublishConfig(): Promise<void> {
        this._isInitializing = true;
        this._hitLoadError = false;
        this._logger.log(this.className, '_getLatestPublishConfig - retrieving config file.');
        try {
            const result = await this._client.getFileContent(PublishConfigService.publishConfigFileName);
            if (result) {
                const config = JSON.parse(result);
                this._publishConfig = config;
                this._publishConfig.publishBranch = config.publishBranch || DEFAULT_PUBLISH_BRANCH;
            }
        } catch (error) {
            this._logger.error(this.className, `_getLatestPublishConfig - Unable to process publish config file, error: ${convertObjectToString(error)}`);
            this._hitLoadError = true;
        }
        this._isInitializing = false;
    }

    private async _configFileExists(): Promise<boolean> {
        const result = await this._client.getFileContent(PublishConfigService.publishConfigFileName);
        return !stringIsNullOrEmpty(result);
    }

    private async _updatePublishConfig(config: IPublishConfig): Promise<ISaveResult> {
        const configFileExists = await this._configFileExists();
        const saveResult = await this._client.createUpdateFileInBranch(
            this._client.workingBranch,
            JSON.stringify(config),
            configFileExists ? stringResources.UpdatePublishBranch : stringResources.CreatePublishBranch,
            joinPaths(this._rootFolder, PublishConfigService.publishConfigFileName),
            !configFileExists
        );
        if (saveResult.status === SaveStatus.Succeeded) {
            this._publishConfig = config;
            this._logger.log(this.className, 'Updating publishConfig', {...this._publishConfig});
        }
        return saveResult;
    }
}
